import {
	BaseQueryFn,
	createApi,
	FetchArgs,
	fetchBaseQuery,
	FetchBaseQueryError,
	retry
} from '@reduxjs/toolkit/query/react';
import config from '../common/config';
import { refreshAccessToken } from '../common/tokenRefresh';
import {RESET_AUTH_ACTION_TYPE, SWAP_TOKEN_URL_PATH} from '../constants';

type ApiAuthState = {
	auth: {
		token: string | null;
	};
};

const AUTHORIZATION_HEADER_NAME = 'Authorization';

const baseQuery = fetchBaseQuery({
	baseUrl: config.remoteApiAccountsUrl,
	prepareHeaders: (headers, { getState }) => {
		const token = (getState() as ApiAuthState).auth.token;
		if (token) {
			headers.set(AUTHORIZATION_HEADER_NAME, `Bearer ${token}`);
		}
		return headers;
	},
});

function getRequestUrl(args: string | FetchArgs): string | undefined {
	return typeof args === 'string' ? args : args.url;
}

const baseQueryWith401Handler: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
	let result = await baseQuery(args, api, extraOptions);

	const url = getRequestUrl(args);
	if (
		result.error?.status === 401
		&& url !== SWAP_TOKEN_URL_PATH
	) {
		const refreshed = await refreshAccessToken();
		if (refreshed) {
			result = await baseQuery(args, api, extraOptions);
		}
		if (result.error?.status === 401) {
			api.dispatch({ type: RESET_AUTH_ACTION_TYPE });
		}
	}

	return result;
};

const baseQueryWithRetry = retry(baseQueryWith401Handler, { maxRetries: 1 });

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
	reducerPath: 'splitApi',
	baseQuery: baseQueryWithRetry,
	tagTypes: [
		'Accounts',
		'Repositories',
		'Repository',
		'RepositoryEntry',
		'RepositoryEntries',
		'RepositoryEntryCount',
		'References',
		'Notes',
		'BoardElements',
		'ApiKeys',
		'AiConversations',
	],
	endpoints: () => ({}),
});
